import React, { useEffect, useRef, useState } from 'react';
import { Ejecutiva, CreateAltaPayload } from '../../types/Altas';
import altasService from '../../services/altasService';
import { Button, Panel, Segmented } from '../ui';
import { CopyIcon, PlusIcon, SpinnerIcon } from '../icons';
import { avisoConsultaDni, esDniValido, inputClass, labelClass, limpiarDni } from './altasUi';

/** Cuánto esperar después de la última tecla antes de consultar Salesforce. */
const ESPERA_CONSULTA_MS = 500;

type Consulta =
  | { estado: 'nada' }
  | { estado: 'consultando' }
  | { estado: 'lista'; texto: string; aviso: boolean }
  | { estado: 'error' };

interface Props {
  ejecutivas: Ejecutiva[];
  planes: string[];
  guardando: boolean;
  onCrear: (payload: CreateAltaPayload) => Promise<boolean>;
  onNuevaEjecutiva: () => void;
  onPegar: () => void;
}

/**
 * Carga de una venta: ejecutiva, DNI, nombre y plan. El plan se elige, no se
 * tipea, porque es lo que después se compara contra Salesforce. Al guardar
 * bien, el DNI y el nombre se limpian y la ejecutiva y el plan quedan: lo
 * normal es cargar varias ventas seguidas de la misma persona.
 */
const NuevaAltaForm: React.FC<Props> = ({
  ejecutivas,
  planes,
  guardando,
  onCrear,
  onNuevaEjecutiva,
  onPegar,
}) => {
  const activas = ejecutivas.filter((e) => e.activa);
  const [ejecutivaId, setEjecutivaId] = useState<string>('');
  const [dni, setDni] = useState('');
  const [nombre, setNombre] = useState('');
  const [plan, setPlan] = useState<string>(planes[0] ?? '');

  // Si la lista cambió (alta nueva o desactivación), que el select apunte a algo válido.
  const ejecutivaValida = activas.some((e) => String(e.id) === ejecutivaId);
  // Sin ejecutiva preseleccionada: si se desactiva la elegida, no salta a otra sin avisar.
  const ejecutivaActual = ejecutivaValida ? ejecutivaId : '';
  const planActual = planes.includes(plan) ? plan : (planes[0] ?? '');

  const dniLimpio = limpiarDni(dni);
  const dniValido = esDniValido(dniLimpio);
  const valido = ejecutivaActual !== '' && dniValido && planActual !== '' && !guardando;

  // Con un DNI válido se le pregunta a Salesforce qué tiene, para avisar antes de
  // cargar (ya tiene el plan, ya lo tuvo y lo dio de baja). Es un aviso: no bloquea.
  const [consulta, setConsulta] = useState<Consulta>({ estado: 'nada' });
  const ultimaConsulta = useRef(0);
  useEffect(() => {
    const pedido = ultimaConsulta.current + 1;
    ultimaConsulta.current = pedido;
    if (!dniValido) {
      setConsulta({ estado: 'nada' });
      return undefined;
    }
    setConsulta({ estado: 'consultando' });
    const timer = setTimeout(async () => {
      try {
        const datos = await altasService.consultarDni(dniLimpio);
        if (pedido !== ultimaConsulta.current) return;
        const aviso = avisoConsultaDni(datos);
        setConsulta(aviso ? { estado: 'lista', ...aviso } : { estado: 'nada' });
      } catch {
        if (pedido === ultimaConsulta.current) setConsulta({ estado: 'error' });
      }
    }, ESPERA_CONSULTA_MS);
    return () => clearTimeout(timer);
  }, [dniLimpio, dniValido]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido) return;
    const ok = await onCrear({
      ejecutivaId: Number(ejecutivaActual),
      dni: dniLimpio,
      nombre: nombre.trim() || undefined,
      plan: planActual,
    });
    if (ok) {
      setDni('');
      setNombre('');
    }
  };

  return (
    <Panel
      title="Nueva alta"
      subtitle="Una venta por DNI. El plan y la fecha reales se completan solos al cruzar con Salesforce."
      actions={
        <>
          <Button
            type="button"
            variant="ghost"
            icon={<CopyIcon />}
            onClick={onPegar}
            disabled={activas.length === 0}
          >
            Pegar desde Excel
          </Button>
          <Button type="button" variant="ghost" icon={<PlusIcon />} onClick={onNuevaEjecutiva}>
            Nuevo Ejecutivo
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-12 md:items-end">
        <div className="md:col-span-3">
          <label htmlFor="alta-ejecutiva" className={labelClass}>
            Ejecutivo
          </label>
          <select
            id="alta-ejecutiva"
            value={ejecutivaActual}
            onChange={(e) => setEjecutivaId(e.target.value)}
            className={inputClass}
            disabled={activas.length === 0}
          >
            <option value="">
              {activas.length === 0 ? 'Cargá un ejecutivo primero' : 'Elegí el ejecutivo'}
            </option>
            {activas.map((e) => (
              <option key={e.id} value={e.id}>
                {e.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2">
          <label htmlFor="alta-dni" className={labelClass}>
            DNI
          </label>
          <input
            id="alta-dni"
            type="text"
            inputMode="numeric"
            value={dni}
            onChange={(e) => setDni(e.target.value)}
            placeholder="Sin puntos"
            autoComplete="off"
            className={`${inputClass} tabular-nums`}
          />
        </div>

        <div className="md:col-span-2">
          <label htmlFor="alta-nombre" className={labelClass}>
            Nombre y apellido
          </label>
          <input
            id="alta-nombre"
            type="text"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Opcional"
            maxLength={120}
            autoComplete="off"
            className={inputClass}
          />
        </div>

        <div className="md:col-span-4">
          <span className={labelClass}>Plan vendido</span>
          <Segmented
            options={planes.map((p) => ({ value: p, label: p }))}
            value={planActual}
            onChange={setPlan}
            ariaLabel="Plan vendido"
          />
        </div>

        <div className="md:col-span-1">
          <Button
            type="submit"
            variant="primary"
            disabled={!valido}
            className="w-full"
            icon={guardando ? <SpinnerIcon /> : <PlusIcon />}
          >
            Cargar
          </Button>
        </div>
      </form>
      {dni !== '' && !dniValido && (
        <p className="mt-2 text-[11px] text-amber-600">
          El DNI tiene que tener entre 6 y 9 dígitos.
        </p>
      )}
      {consulta.estado === 'lista' && (
        <p className={`mt-2 text-xs ${consulta.aviso ? 'text-amber-700' : 'text-slate-500'}`} role="status">
          {consulta.texto}
        </p>
      )}
      {consulta.estado === 'error' && (
        <p className="mt-2 text-[11px] text-slate-400" role="status">
          No se pudo consultar Salesforce; el cruce lo va a revisar al cargar.
        </p>
      )}
    </Panel>
  );
};

export default NuevaAltaForm;
